import { IconButton, ReferenceArea } from '@/components/admin/reglages/reference-list';
import { duplicateRuleRow, ruleDuplicationBlocked } from '@/lib/duplication';
import { moveRow } from '@/lib/persona-settings';
import type { RuleRow } from '@/lib/rules-settings';
import { newRuleRow, rulesBlocked } from '@/lib/rules-settings';
import { GLOBAL_RULE_TEXT_MAX, RULES_CONFLICT_SENTENCE } from '@matter_extractor/shared';
import { Alert, Icon, Tooltip } from '@pms-digital/design-system';
import type { DragEvent as ReactDragEvent } from 'react';
import { useId, useState } from 'react';

/**
 * Section 1 of Réglages > Règles: the global rules (§8.5, §11.3).
 *
 * The directions served to the interviewer, in a list whose ORDER is its
 * priority - « en cas de conflit, la règle la plus haute prime », which is the
 * sentence the callout below carries, spelled once in `shared` and served both
 * to this screen and to the model (§10.3).
 *
 * ## It holds NO draft: the draft of « Les règles » is the layer's
 *
 * Since issue #192 (vitrine.md section 5) the rules and the guardrails are one
 * versioned object, so this list is a FORM over the rows of ITS draft: it reads
 * them from its props and hands every change back whole (`onChange`), and the
 * layer above autosaves them, compares them with the version, and writes the one
 * « Enregistrer une nouvelle version ». Nothing here is saved by a button of its
 * own, and nothing here is immediate - the deletion included: the trash takes
 * the row out of the draft, the history keeps the version that had it, and the
 * undo of the head puts it back.
 *
 * ## Arrows AND a native drag
 *
 * Two ways to move a rule, and both are wanted: the grip is what an operator
 * reaches for on a list of five, the arrows work from the keyboard and can NAME
 * what they do (« Descendre la règle 2 ») where a handle names nothing. The
 * drag is the native one of the protocol timeline (`objective-list.tsx`), with
 * no library: the grip is the drag SOURCE and the row is the drop target, which
 * is what keeps a drag from starting inside the textarea and stealing a
 * selection.
 *
 * A version opened by « Voir » is `readOnly`: the whole section is `inert`, so
 * nothing in it can be focused, clicked or dragged.
 */

export interface GlobalRulesProps {
  /** The rules of the draft, by priority. */
  readonly rules: readonly RuleRow[];
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readonly readOnly: boolean;
  /** The next list, whole: a rewrite, a creation, a removal or a move. */
  readonly onChange: (rows: RuleRow[]) => void;
}

/** What a drag is carrying and where it would land. */
type Drag = { readonly key: string; readonly overKey: string | null; readonly after: boolean };

export function GlobalRules({ rules: rows, readOnly, onChange }: GlobalRulesProps) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const headingId = useId();

  const blocked = rulesBlocked(rows);
  const duplicationBlocked = ruleDuplicationBlocked(rows);

  function drop(targetKey: string): void {
    if (drag === null) return;
    const from = rows.findIndex((one) => one.id === drag.key);
    let to = rows.findIndex((one) => one.id === targetKey);
    setDrag(null);
    if (from < 0 || to < 0 || from === to) return;
    // `to` is an index in the list WITHOUT the dragged row: dropping after a
    // row that sits below the dragged one is the case the two spellings
    // disagree on. Same arithmetic as `objective-list.tsx`.
    if (from < to) to -= 1;
    const target = drag.after ? to + 1 : to;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(Math.min(target, next.length), 0, moved);
    onChange(next);
  }

  return (
    <section
      aria-labelledby={headingId}
      data-testid="reglages-regles"
      className="space-y-4"
      {...(readOnly ? { inert: true } : {})}
    >
      <header className="space-y-1">
        <h3 id={headingId} className="font-semibold text-bleu-nuit text-body-lg leading-7">
          Règles globales
        </h3>
        <p className="max-w-3xl text-gray-700 text-small">
          Des directions servies à l’interviewer, à chaque tour de chaque session. L’ordre est leur
          priorité : la première prime. Le scribe les observe aussi et signale celles que le dernier
          message a enfreintes, sans aucune action.
        </p>
      </header>

      <ol className="space-y-3">
        {rows.map((row, index) => {
          const hint = drag !== null && drag.overKey === row.id;
          const dropClass = hint
            ? `border-bleu-nuit ${drag.after ? 'border-b-4' : 'border-t-4'}`
            : 'border-transparent';
          return (
            <li
              key={row.id}
              data-testid="reglages-regle"
              onDragOver={(event: ReactDragEvent<HTMLElement>) => {
                if (drag === null) return;
                event.preventDefault();
                const box = event.currentTarget.getBoundingClientRect();
                const after = event.clientY > box.top + box.height / 2;
                setDrag((was) => (was === null ? null : { ...was, overKey: row.id, after }));
              }}
              onDrop={(event: ReactDragEvent<HTMLElement>) => {
                event.preventDefault();
                drop(row.id);
              }}
              className={`flex items-start gap-2 border-2 ${dropClass} ${
                drag?.key === row.id ? 'opacity-40' : ''
              }`}
            >
              {/* The drag SOURCE, and the only one: a `draggable` row would
                  start a drag on a click inside the textarea. The drawn bubble,
                  like every other one of this application (#103). */}
              <Tooltip content="Glisser pour réordonner">
                <span
                  draggable
                  data-testid="reglages-regle-poignee"
                  onDragStart={() => setDrag({ key: row.id, overKey: null, after: false })}
                  onDragEnd={() => setDrag(null)}
                  className="mt-2 flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-md bg-gray-100 font-semibold text-gray-600 text-small"
                >
                  <span className="inline-flex text-gray-400">
                    <Icon name="drag_indicator" size={16} />
                  </span>
                  <span className="sr-only">Règle {index + 1}</span>
                </span>
              </Tooltip>

              <div className="min-w-0 flex-1 space-y-1">
                <ReferenceArea
                  label={index === 0 ? 'Règle 1 - la plus prioritaire' : `Règle ${index + 1}`}
                  testId="reglages-regle-texte"
                  value={row.text}
                  max={GLOBAL_RULE_TEXT_MAX}
                  placeholder="Une question par tour."
                  onChange={(text) =>
                    onChange(rows.map((one) => (one.id === row.id ? { ...one, text } : one)))
                  }
                />
              </div>

              <div className="flex shrink-0 flex-col gap-1">
                <IconButton
                  label={`Monter la règle ${index + 1}`}
                  disabled={index === 0}
                  onClick={() => onChange(moveRow([...rows], index, -1))}
                >
                  <Icon name="arrow_upward" size={16} />
                </IconButton>
                <IconButton
                  label={`Descendre la règle ${index + 1}`}
                  disabled={index === rows.length - 1}
                  onClick={() => onChange(moveRow([...rows], index, 1))}
                >
                  <Icon name="arrow_downward" size={16} />
                </IconButton>
                {/*
                  The copy icon, just before the trash (§4.2). The copy is a row
                  with a NEW identifier, put just after its source: the rules
                  below it drop one priority rank - expected rather than
                  surprising, since the array's order IS the priority (§11.3).

                  This screen's own `IconButton` and not `DuplicateButton`: the
                  three controls it stacks with are bordered icon buttons, and a
                  second spelling of that recipe would drift from this one. The
                  glyph is the same `content_copy` as everywhere else.
                */}
                <IconButton
                  label={`Dupliquer la règle ${index + 1}`}
                  blocked={duplicationBlocked}
                  testId="reglages-regle-dupliquer"
                  onClick={() => onChange(duplicateRuleRow(rows, index) ?? [...rows])}
                >
                  <Icon name="content_copy" size={16} />
                </IconButton>
                <IconButton
                  label={`Retirer la règle ${index + 1}`}
                  tone="danger"
                  testId="reglages-regle-retirer"
                  onClick={() => onChange(rows.filter((one) => one.id !== row.id))}
                >
                  <Icon name="delete" size={16} />
                </IconButton>
              </div>
            </li>
          );
        })}
      </ol>

      {rows.length === 0 ? (
        <p className="text-gray-600 text-small">
          Aucune règle globale : l’interviewer ne reçoit rien de cette liste.
        </p>
      ) : null}

      <div className="flex items-start gap-2">
        <span aria-hidden="true" className="block w-8 shrink-0" />
        <button
          type="button"
          data-testid="reglages-regle-ajouter"
          onClick={() => onChange([...rows, newRuleRow()])}
          className="flex h-13 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white font-semibold text-bleu-nuit text-body hover:bg-gray-50"
        >
          <Icon name="add" size={18} />
          Ajouter une règle
        </button>
        <span aria-hidden="true" className="block w-9 shrink-0" />
      </div>

      {blocked === null ? null : (
        <p data-testid="reglages-regles-bloquees" className="text-gray-700 text-small">
          {blocked}
        </p>
      )}

      <Alert tone="info">
        <p>
          {RULES_CONFLICT_SENTENCE} Retirer une règle la sort de la prochaine version : l’historique
          garde celle qui l’avait, et les sessions déjà commencées gardent la version qu’elles ont
          figée.
        </p>
      </Alert>
    </section>
  );
}
